import analyticstracker from "@jmc/analyticstracker";
import { useEffect, useState } from "react";

export interface UsePageLoadedTrackingOptions {
    /** Delay in milliseconds after page loads before executing tracking (default: 0) */
    delay?: number;
    /** Whether to check if page is loaded before tracking (default: true) */
    waitForPageLoad?: boolean;
    /** Additional conditions that must be true before tracking */
    conditions?: boolean[];
    /** Callback function to execute when all conditions are met */
    onTrack?: () => void;
}

export interface UsePageLoadedTrackingReturn {
    /** Whether the page is loaded */
    isPageLoaded: boolean;
    /** Whether tracking should execute based on all conditions */
    shouldTrack: boolean;
    /** Function to manually trigger tracking */
    executeTracking: (eventType: string, options?: unknown) => void;
}

/**
 * Custom hook that manages page load state and executes analytics tracking
 * only after the page is fully loaded and all conditions are met.
 *
 * @param options Configuration options for the tracking behavior
 * @returns Object containing page load state and tracking utilities
 *
 * @example
 * ```tsx
 * const { shouldTrack, executeTracking } = usePageLoadedTracking({
 *   delay: 500,
 *   conditions: [isVisible, interacted],
 *   onTrack: () => console.log('Tracking executed')
 * });
 *
 * useEffect(() => {
 *   if (shouldTrack) {
 *     executeTracking(EventTypes.NAVIGATION_IMPRESSION, { checkVisibility: true });
 *   }
 * }, [shouldTrack]);
 * ```
 */
export const usePageLoadedTracking = (options: UsePageLoadedTrackingOptions = {}): UsePageLoadedTrackingReturn => {
    const { delay = 0, waitForPageLoad = true, conditions = [], onTrack } = options;

    const [isPageLoaded, setIsPageLoaded] = useState(false);

    // Check if page is loaded and setup load event listener
    useEffect(() => {
        if (!waitForPageLoad) {
            setIsPageLoaded(true);
            return;
        }

        const handleLoad = (): void => {
            setIsPageLoaded(true);
        };

        // Check if the page is already loaded
        if (document.readyState === "complete" || document.readyState === "interactive") {
            handleLoad();
        } else {
            // If not loaded, add the listener
            window.addEventListener("load", handleLoad);
        }

        // Cleanup
        return () => {
            window.removeEventListener("load", handleLoad);
        };
    }, [waitForPageLoad]);

    // Calculate if all conditions are met for tracking
    const allConditionsMet = conditions.length === 0 || conditions.every((condition) => condition === true);
    const shouldTrack = isPageLoaded && allConditionsMet;

    // Function to execute tracking with delay
    const executeTracking = (eventType: string, trackingOptions?: unknown): void => {
        setTimeout(() => {
            analyticstracker().trackImpression(eventType, trackingOptions);
            onTrack?.();
        }, delay);
    };

    return {
        isPageLoaded,
        shouldTrack,
        executeTracking,
    };
};
